import { LOCALES } from "@/lib/locale";
import { z } from "zod";
import { LOCATION_SLUGS } from "./provinces";
import { LANGUAGE_CODES } from "./lang-utils";
import { MAX_SECONDARY_CATEGORIES } from "./listing-visibility";
import { REQUEST_CATEGORY_OPTIONS } from "./requests";
import { INTENTION_VALUES, INTENTION_OTHER_MAX } from "./onboarding";
import { COUNTRY_CODES } from "./data/countries";
import { CLIENT_TYPES } from "./client-types";

// ── Primitives ───────────────────────────────────────────────────────────────

export const uuidSchema = z.string().uuid();

export const emailSchema = z
  .string()
  .email()
  .max(255)
  .transform((s) => s.toLowerCase().trim());

export const shortTextSchema = z.string().trim().min(1).max(500);

export const longTextSchema = z.string().trim().min(1).max(5000);

export const ratingSchema = z.coerce.number().int().min(1).max(5);

// ── Years of experience ─────────────────────────────────────────────────────
//
// Single source of truth for the apply form's "Years of Experience" select and
// the corresponding server-side enum check. Keeping these in lockstep is the
// whole point — see validations.ts vs ApplicationForm options drift that
// silently failed the schema's old `.max(10)` for "10–15 years" (11 chars).

export const YEARS_EXPERIENCE_OPTIONS = [
  "1–3 years",
  "3–5 years",
  "5–10 years",
  "10–15 years",
  "15+ years",
] as const;

export type YearsExperience = (typeof YEARS_EXPERIENCE_OPTIONS)[number];

// ── Zod-error helper ────────────────────────────────────────────────────────
//
// Returns a human-readable message for the first issue in a ZodError, prefixed
// with the offending field path. Used by server actions to surface validation
// failures to the form's red `<p role="alert">` (e.g. ApplicationForm,
// ContactForm, FeedbackWidget). Replaces the generic "Please fill in all
// required fields with valid data." that previously hid which field actually
// failed. For per-field inline errors the form's own client-side
// `validateStep()` handles labeling — this helper is for server-action returns.

/**
 * Walk a Zod issue path, humanizing each segment.
 *   ["yearsExperience"]              → "Years Experience"
 *   ["pricingTiers", 0, "name"]      → "Service Packages · row 1 · Name"
 *   ["secondaryCategories", 0]       → "Secondary Categories · row 1"
 */
// Overrides for fields whose code identifier doesn't match the user-facing
// label. Keep this small — most fields work fine with camelCase splitting;
// only add an entry when the UI label diverges (e.g. `serviceDescription` is
// labelled "Professional Bio" on the apply form, not "Service Description").
const FIELD_LABELS: Record<string, string> = {
  serviceDescription: "Professional Bio",
  previousExperience: "Previous Experience",
  pricingTiers: "Service Packages",
};

function humanizePath(path: PropertyKey[]): string {
  if (path.length === 0) return "";
  const parts = path.map((seg) => {
    if (typeof seg === "number") return `row ${seg + 1}`;
    const key = String(seg);
    if (FIELD_LABELS[key]) return FIELD_LABELS[key];
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (c) => c.toUpperCase())
      .trim();
  });
  return parts.join(" · ");
}

export function firstIssueMessage(error: z.ZodError): string {
  const issue = error.issues[0];
  if (!issue) return "Please fill in all required fields with valid data.";
  const field = humanizePath(issue.path);
  return field ? `${field}: ${issue.message}` : issue.message;
}

/**
 * Minimal structural shape of a next-intl translator — declared here so this
 * module (also imported by client code) needn't depend on next-intl. Server
 * actions pass their `getTranslations("validationErrors")` result, casting
 * through `as unknown as ZodMessageT` to bridge next-intl's strict key typing
 * (the keys we look up are dynamic — issue codes + `fields.<key>`).
 */
export interface ZodMessageT {
  (key: string, values?: Record<string, string | number>): string;
  has(key: string): boolean;
}

/** Localized field label for one issue-path segment (number → "row N"). Falls
 *  back to the humanized English key when the catalogue has no `fields.<key>`. */
function localizeSegment(seg: PropertyKey, t: ZodMessageT): string {
  if (typeof seg === "number") return t("row", { n: seg + 1 });
  const key = String(seg);
  if (t.has(`fields.${key}`)) return t(`fields.${key}`);
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();
}

/** Map a Zod issue to a localized message by its CODE — sidesteps localizing
 *  every schema's bespoke English `.message`. Custom refinements (phone, etc.)
 *  collapse to the generic "check this field" (acceptable on this edge path:
 *  the client validates inline first with proper localized messages). */
function localizeIssue(issue: z.core.$ZodIssue, t: ZodMessageT): string {
  switch (issue.code) {
    case "too_small":
      // min 1 (string/array) reads as "required"; larger mins as "too short".
      return Number(issue.minimum) <= 1 ? t("required") : t("tooShort", { min: Number(issue.minimum) });
    case "too_big":
      return t("tooLong", { max: Number(issue.maximum) });
    case "invalid_format":
      // Zod 4 surfaces email/regex failures here, carrying a `format` tag.
      return issue.format === "email" ? t("invalidEmail") : t("invalidValue");
    case "invalid_type":
      // A missing required field parses as invalid_type (expected X, got undefined).
      return t("required");
    case "invalid_value":
      // Replaces v3 invalid_enum_value — enum / literal mismatch.
      return t("invalidOption");
    default:
      return t("invalidValue");
  }
}

/**
 * Locale-aware counterpart to `firstIssueMessage`. Builds "<localized field>:
 * <localized message>" from the first issue. Used by the server-action
 * fallback path (contact/application/request/feedback) where the parse fails
 * server-side; the form's inline client validation is the primary surface.
 */
export function localizeZodError(error: z.ZodError, t: ZodMessageT): string {
  const issue = error.issues[0];
  if (!issue) return t("generic");
  const field = issue.path.map((seg) => localizeSegment(seg, t)).join(" · ");
  const message = localizeIssue(issue, t);
  return field ? t("withField", { field, message }) : message;
}

// ── Email ───────────────────────────────────────────────────────────────────

/** Simple client-side email format check */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  if (!trimmed) return true; // empty is valid (optionality handled elsewhere)
  return EMAIL_RE.test(trimmed);
}

// ── Phone ───────────────────────────────────────────────────────────────────

/**
 * Validate a phone number string.
 * Accepts Vietnamese (+84 / 0-prefix) and international (+country code) formats.
 * Format: optional country code (+1 to +999), optional area code in parens,
 * then 7–15 characters of digits/spaces/hyphens for the local number.
 */
const PHONE_RE = /^(\+\d{1,3}\s?)?(\(\d{1,4}\)\s?)?[\d\s-]{7,15}$/;

export function isValidPhone(phone: string): boolean {
  const trimmed = phone.trim();
  if (!trimmed) return true; // empty is valid (optionality handled elsewhere)
  if (!PHONE_RE.test(trimmed)) return false;
  // Ensure at least 7 actual digits (not just spaces/hyphens)
  const digitCount = trimmed.replace(/[^\d]/g, "").length;
  return digitCount >= 7 && digitCount <= 15;
}

/**
 * A website a professional can publish on their listing.
 *
 * The value is rendered straight into an `href` on the public profile and the
 * dashboard, so it must be a real web address and not merely a 500-character
 * string — `contact.facebook` has had a host check since it was added, and this
 * field had none (audit 2026-09-02, #11).
 *
 * ⚠ A BARE DOMAIN MUST STAY VALID. Both render sites already do
 * `startsWith("http") ? value : "https://" + value`, so listings hold values
 * like `example.com` today, and `updateContactAction` re-validates every field
 * on each save. Demanding a scheme would have locked those pros out of editing
 * ANY contact field until they noticed and fixed a URL nobody told them was
 * wrong — the same shape as the missing-language lock that made a new
 * admin-created listing uneditable.
 *
 * So: normalise the way the renderer does, then require the result to parse and
 * to carry an http(s) scheme. That rejects `javascript:`, `data:` and friends
 * while accepting what pros actually type.
 */
export function isValidWebsite(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true; // empty is valid (optionality handled elsewhere)
  if (trimmed.length > 500) return false;
  // Reject a scheme-looking prefix that is not http(s) BEFORE normalising —
  // `javascript:alert(1)` does not start with "http", so the renderer would
  // prepend https:// and defuse it, but storing it is still wrong and the next
  // render site to forget the prefix would ship the hole.
  //
  // ⚠ A PORT LOOKS EXACTLY LIKE A SCHEME to a naive prefix test: dots and
  // digits are legal scheme characters, so the first version of this line read
  // `example.com:8080` as the scheme "example.com" and rejected a perfectly
  // ordinary address (review 2026-09-02). What follows the colon is the tell —
  // a port is digits, then either the end or a path.
  // `[\s\S]` rather than `.` with the `s` flag: dotAll needs an es2018 target
  // and this project's is lower, so `/is` is a compile error (caught by tsc
  // AFTER vitest went green — vitest does not typecheck).
  const scheme = trimmed.match(/^([a-z][a-z0-9+.-]*):([\s\S]*)$/i);
  if (scheme && !/^https?$/i.test(scheme[1]) && !/^\d+([/?#]|$)/.test(scheme[2])) return false;
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    // A host with a dot and no spaces — `new URL` happily accepts "https://x".
    return /^[^\s]+\.[^\s.]+$/.test(url.hostname);
  } catch {
    return false;
  }
}

/** Allowed characters in phone input: digits, +, spaces, hyphens, parens */
const PHONE_ALLOWED = /[\d+\s\-()]/;

/** Filter a phone input's onChange value to only allowed characters */
export function filterPhoneInput(value: string): string {
  return value.split("").filter((c) => PHONE_ALLOWED.test(c)).join("");
}

/** Zod schema for optional phone — empty string passes, non-empty must be valid */
export const phoneSchema = z.string().max(50)
  .refine((v) => isValidPhone(v), {
    message: "Please enter a valid phone number (7–15 digits, may include +, spaces, hyphens).",
  })
  .default("");

// ── Search ───────────────────────────────────────────────────────────────────

export const searchParamsSchema = z.object({
  q: z.string().max(200).default(""),
  category: z.string().max(100).default(""),
  // Empty string = "no location filter"; otherwise must be a known slug.
  // The `.catch("")` is *deliberately* scoped to URL-derived input —
  // stale label URLs (`?location=Hanoi`, `?location=Other`, …) silently
  // degrade to "no filter" rather than producing a zero-results dead
  // end. Don't copy this `.catch` pattern to schemas that validate
  // server-trusted payloads; we want loud errors there.
  location: z
    .union([z.literal(""), z.enum(LOCATION_SLUGS as unknown as [string, ...string[]])])
    .catch("")
    .default(""),
  language: z.string().max(100).default(""),
  sort: z
    .enum(["recommended", "rating", "reviews", "experience"])
    .default("recommended"),
  offset: z.coerce.number().int().min(0).max(10000).default(0),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  // The "Recommended" language boost, computed server-side on the /search page
  // (logged-in client → preferred language / English) and echoed back by the
  // load-more request so paged results keep page 1's order WITHOUT the API route
  // re-reading the session on every scroll. Trusting it from the client is safe:
  // it only reorders the requester's OWN results — strictly less powerful than
  // the `language` filter they already control. A junk value just matches no
  // pro and yields no boost. "" / absent = no boost.
  boostLanguage: z.string().max(10).optional(),
  // Viewer locale, for affiliate card text on load-more pages. Anything else
  // reads English, the default copy.
  // Derived, so a routed locale is accepted here without a second list to
  // remember. `.catch("en")` still absorbs anything unrecognised.
  locale: z.enum(LOCALES).catch("en").default("en"),
});

// ── Messages ─────────────────────────────────────────────────────────────────

export const sendMessageSchema = z.object({
  conversationId: z
    .string()
    .uuid()
    .optional()
    .or(z.literal(""))
    .transform((v) => v || undefined),
  professionalId: z
    .string()
    .uuid()
    .optional()
    .or(z.literal(""))
    .transform((v) => v || undefined),
  body: z.string().trim().min(1).max(5000),
});

// ── Contact Form ─────────────────────────────────────────────────────────────

export const contactSchema = z
  .object({
    name: z.string().trim().min(1, "Please enter your name.").max(500),
    email: z.string().max(255)
      .transform((s) => s.trim().toLowerCase())
      .pipe(z.string().email("Please enter a valid email address.").or(z.literal("")))
      .default(""),
    phone: phoneSchema,
    message: z.string().trim().min(1, "Please describe your requirements.").max(5000),
    service: z.string().min(1, "Please select a service.").max(200),
    professionalId: uuidSchema,
  })
  .refine((data) => data.email !== "" || data.phone.trim() !== "", {
    message: "Please provide at least an email address or a phone number.",
    path: ["email"],
  });

// ── General "Get in touch" contact form (public /contact page) ────────────────
// Public (no auth) → emails the team. `type` is an optional enquiry category;
// an empty/invalid value collapses to "other".
export const ENQUIRY_TYPES = ["client", "professional", "partnership", "other"] as const;
export const generalContactSchema = z.object({
  name: z.string().trim().min(1, "Please enter your name.").max(120),
  email: z.string().max(255)
    .transform((s) => s.trim().toLowerCase())
    .pipe(z.string().email("Please enter a valid email address.")),
  subject: z.string().trim().min(1, "Please enter a subject.").max(200),
  type: z.enum(ENQUIRY_TYPES).catch("other"),
  message: z.string().trim().min(1, "Please enter a message.").max(5000),
});

// ── Pro dashboard renewal/upgrade request (confirm-your-info modal) ───────────
// Authed pro confirms their contact info; identity (business/plan/status) is
// read server-side from the session, never trusted from the form.
export const subscriptionContactSchema = z
  .object({
    name: z.string().trim().min(1, "Please enter your name.").max(120),
    email: z.string().max(255)
      .transform((s) => s.trim().toLowerCase())
      .pipe(z.string().email("Please enter a valid email address.").or(z.literal("")))
      .default(""),
    phone: phoneSchema,
    message: z.string().trim().max(2000).optional().default(""),
    intent: z.enum(["renew", "upgrade"]).catch("renew"),
  })
  .refine((d) => d.email !== "" || d.phone.trim() !== "", {
    message: "Please provide at least an email address or a phone number.",
    path: ["email"],
  });

// ── Matching request ─────────────────────────────────────────────────────────
//
// Vocabulary (categories / statuses / SLA) lives in
// `src/lib/requests.ts` — the DB CHECK constraints mirror those values.
//
// Every field is compulsory (product decision 2026-06-11). The timeline field
// was removed 2026-10-01 (owner): it filtered nothing. Unknown slugs
// error loudly — this validates a server-trusted payload, so the URL-shaped
// `.catch` leniency of searchParamsSchema is NOT wanted here.
export const requestSchema = z.object({
  category: z.enum(REQUEST_CATEGORY_OPTIONS as unknown as [string, ...string[]], {
    message: "Please select a category.",
  }),
  location: z.enum(LOCATION_SLUGS as unknown as [string, ...string[]], {
    message: "Please select a province / city.",
  }),
  description: z
    .string()
    .trim()
    .min(20, "Please give us at least a couple of sentences (20 characters minimum).")
    .max(2000, "Please keep it under 2,000 characters."),
  // Optional since 2026-08-26 (owner): the reply channel is the account email
  // or the guest's typed email — a mandatory second channel was friction.
  // Empty passes; non-empty must be a real number (shared phoneSchema).
  phone: phoneSchema,
  // Acceptance of the Terms and the Privacy Policy (owner, 2026-10-13): asking us
  // to connect you is consent to share your details with that professional, and
  // this box is where it is recorded (requests.terms_accepted_at). The action
  // checks it first with its own message; this keeps the schema honest for any
  // other caller.
  acceptTerms: z.literal(true, { message: "Please accept the Terms of Service and Privacy Policy." }),
});

// ── Client enrichment (onboarding) ───────────────────────────────────────────

// Every field is optional — the whole enrichment form is skippable. "" means
// "not set" (mirrors the searchParams pattern). Stored in client_preferences;
// intentionOther is only meaningful when intention === "other" (the action
// clears it otherwise). Validates against the same vocabularies the pickers use
// so a tampered POST can't write a junk slug/code.
/** The guest variant (2026-08-26): /request is open to unauthenticated
 *  visitors, and a typed email is their only reply channel — required.
 *  Authenticated submissions keep the account email and never send this. */
export const guestRequestSchema = requestSchema.extend({
  email: emailSchema,
});

export const clientPreferencesSchema = z.object({
  intention: z
    .union([z.literal(""), z.enum(INTENTION_VALUES as unknown as [string, ...string[]])])
    .default(""),
  intentionOther: z.string().trim().max(INTENTION_OTHER_MAX).default(""),
  targetCity: z
    .union([z.literal(""), z.enum(LOCATION_SLUGS as unknown as [string, ...string[]])])
    .default(""),
  language: z
    .union([z.literal(""), z.enum(LANGUAGE_CODES as unknown as [string, ...string[]])])
    .default(""),
  country: z
    .union([z.literal(""), z.enum(COUNTRY_CODES as unknown as [string, ...string[]])])
    .default(""),
});

export type ClientPreferencesInput = z.infer<typeof clientPreferencesSchema>;

// ── Feedback ─────────────────────────────────────────────────────────────────

// DB check constraint: category IN ('bug', 'feature', 'general')
export const feedbackSchema = z.object({
  category: z
    .string()
    .default("general")
    .transform((s) => s.trim().toLowerCase())
    .pipe(z.enum(["bug", "feature", "general"])),
  message: longTextSchema,
  email: emailSchema,
});

// ── Reviews ──────────────────────────────────────────────────────────────────

export const reviewSchema = z.object({
  professionalId: uuidSchema,
  proSlug: z.string().min(1).max(200),
  rating: ratingSchema,
  text: z.string().max(5000).optional().default(""),
  authorRole: z.string().max(200).optional().default(""),
  authorCompany: z.string().max(200).optional().default(""),
});

// ── Profile ──────────────────────────────────────────────────────────────────

export const profileInfoSchema = z.object({
  displayName: z.string().max(200).nullable(),
  title: z.string().max(200).nullable(),
  // Bio supports markdown — 6000 to give pros room for headings, lists, links
  // on top of prose. Apply form's `serviceDescription` stays plain text at 5000.
  about: z.string().max(6000).nullable(),
  // Pro-editable, self-reported background (own columns on `professionals`).
  // Plain text (newline-separated entries); generous caps.
  education: z.string().max(2000).nullable(),
  previousExperience: z.string().max(2000).nullable(),
  memberships: z.string().max(2000).nullable(),
  category: z.string().max(100).nullable(),
  secondaryCategories: z.array(z.string().max(100)).max(MAX_SECONDARY_CATEGORIES).default([]),
  availability: z
    .enum([
      "Available now — accepting new clients",
      "Limited availability — contact to enquire",
      "Fully booked — waitlist only",
    ])
    .nullable(),
  // NOTE: no `location` key — professionals.location is a trigger-maintained
  // mirror of the main office row; it's edited via updateOfficesAction only.
  specialties: z.array(z.string().max(100)).max(20).default([]),
  // Ranked subset of the fixed client-type vocabulary (array order = priority).
  // Internal matching signal; owner-writable, not on the public profile.
  clientTypes: z.array(z.enum(CLIENT_TYPES)).max(CLIENT_TYPES.length).default([]),
  // Language codes (ISO-style). At least one is required to keep parity with
  // the apply form's "≥1 language" rule. Whitelist enforced via the
  // language-codes registry.
  languages: z
    .array(z.enum(LANGUAGE_CODES as unknown as [string, ...string[]]))
    .min(1, "Select at least one language.")
    .max(LANGUAGE_CODES.length),
  // Years of experience — apply form captures it as a string and parses;
  // the dashboard sends a clean integer. 0–100 is the sane bound (anything
  // over 100 isn't reliable for a Vietnam-based pro and is almost certainly
  // a typo or paste error).
  yearsExperience: z
    .number()
    .int("Years of experience must be a whole number.")
    .min(0, "Years of experience can't be negative.")
    .max(100, "Years of experience must be 100 or fewer.")
    .nullable(),
});

// ── Application ──────────────────────────────────────────────────────────────

export const applicationSchema = z.object({
  fullName: shortTextSchema,
  email: emailSchema,
  businessName: z.string().max(300),
  registrationNumber: z.string().max(100),
  // Required and must be a known city slug (matches the apply form's
  // dropdown options). Server-side enforcement so a crafted POST can't
  // bypass the UI's whitelist and inject arbitrary strings into
  // `professional_applications.city` (which then propagates to
  // `professionals.location` on approval).
  city: z.enum(LOCATION_SLUGS as unknown as [string, ...string[]]),
  phone: phoneSchema,
  website: z.string().max(500).refine(isValidWebsite, "Enter a valid website address").or(z.literal("")),
  licenseNumber: z.string().max(100),
  issuingAuthority: z.string().max(300),
  yearsExperience: z.enum(YEARS_EXPERIENCE_OPTIONS as unknown as [string, ...string[]]),
  category: shortTextSchema,
  secondaryCategories: z.array(z.string().max(100)).max(MAX_SECONDARY_CATEGORIES).default([]),
  languages: z.array(z.string().max(10)).max(20),
  specialties: z.string().max(2000),
  clientTypes: z.array(z.enum(CLIENT_TYPES)).max(CLIENT_TYPES.length).default([]),
  serviceDescription: z.string().max(5000),
  pricingTiers: z
    .array(
      z.object({
        name: z.string().max(200),
        price: z.string().max(100),
        description: z.string().max(500),
      }),
    )
    .max(10),
  availability: z.string().max(200),
});

// ── Change password ──────────────────────────────────────────────────────────
//
// Strength rules duplicated here (instead of imported from `password-rules.ts`)
// so the schema is purely declarative and doesn't pull a `.refine()` that calls
// out to another module. The two sources are tiny and intentional — keep them
// in lockstep when changing the policy. Same lockstep applies to Supabase's
// hosted password-policy setting; bump that too.
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required."),
  newPassword: z
    .string()
    .min(10, "Password must be at least 10 characters.")
    .regex(/[A-Z]/, "Password must contain at least 1 uppercase letter.")
    .regex(/[0-9]/, "Password must contain at least 1 number."),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  path: ["confirmPassword"],
  message: "Passwords do not match.",
});

// ── Leads ────────────────────────────────────────────────────────────────────

export const trackLeadSchema = z.object({
  professionalId: uuidSchema,
  type: z.enum(["profile_view", "package_click"]),
});

// ── Visibility ───────────────────────────────────────────────────────────────

export const visibilitySchema = z.object({
  visible: z
    .string()
    .transform((v) => v === "true"),
});

// ── Admin: decide application ────────────────────────────────────────────────

export const decideApplicationSchema = z.object({
  applicationId: uuidSchema,
  decision: z.enum(["approved", "rejected"]),
});

// ── Admin: moderate review ───────────────────────────────────────────────────

export const moderateReviewSchema = z.object({
  reviewId: uuidSchema,
  decision: z.enum(["approved", "rejected"]),
});

// ── Admin: subscription status ───────────────────────────────────────────────

export const subscriptionStatusSchema = z.object({
  professionalId: uuidSchema,
  status: z.enum(["trial", "active", "past_due", "cancelled"]),
});
